/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Live transfer progress, read from the task progress updates Swift's providers write
 * (providers/coriolis_common/progress.py). Each disk gets ONE progress update whose message is
 * kept up to date, e.g.
 *
 *   Transferring vol-0abc (30.0 GiB disk, full, downloaded by the minion): 12.4 of 29.9 GiB copied (41%) · 8.6 MiB/s · ~35 min left
 *   Transferring Hard disk 1 (20.0 GiB disk, changes since last sync): at 6.0 of 20.0 GiB of the disk (30%) · 55.2 MiB/s · ~6 min left
 *   Transferring vol-0abc (30.0 GiB disk, full, downloaded by the minion): complete, 29.9 GiB copied in 56 min (9.1 MiB/s)
 *   Scanning disk-1 (40.0 GiB disk, finding changes, on the Hyper-V helper VM): 12.0 of 40.0 GiB read (30%) · 80.0 MiB/s · ~5 min left
 *
 * The update's current_step / total_steps carry the exact byte counts, so they refine the
 * rounded figures of the message when they agree with it. Everything else in a task's progress
 * updates is free text (OS preparation steps and so on) and is left alone.
 */

import type { Task, ProgressUpdate } from "@src/@types/Task";

export const KIB = 1024;
export const MIB = 1024 * 1024;
export const GIB = 1024 * 1024 * 1024;
export const TIB = 1024 * GIB;

const UNITS: { [unit: string]: number } = {
  B: 1,
  KiB: KIB,
  MiB: MIB,
  GiB: GIB,
  TiB: TIB,
};

export type DiskProgress = {
  /** "Transferring", "Scanning", ... (the first word of the message). */
  action: string;
  /** True for the read pass that finds what to copy (action "Scanning"). */
  scanning: boolean;
  disk: string;
  diskSizeBytes: number | null;
  /** "full", "changes since last sync", "finding changes", ... */
  mode: string;
  /** Where the copy runs, e.g. "downloaded by the minion". */
  via: string | null;
  /** The copy reports how far into the disk it is ("at X of Y GiB of the disk"), not data copied. */
  position: boolean;
  doneBytes: number;
  totalBytes: number | null;
  /** 0..100 */
  percent: number;
  rateBytesPerSec: number | null;
  etaSeconds: number | null;
  complete: boolean;
  /** How long the copy took (complete messages only). */
  durationSeconds: number | null;
};

export const PHASES = [
  "Validating",
  "Preparing",
  "Syncing disks",
  "OS preparation",
  "Deploying",
  "Finalising",
] as const;
export type WorkPhase = (typeof PHASES)[number];
export type Phase = WorkPhase | "Queued" | "Completed" | "Failed" | "Cancelled";

export type DiskRow = DiskProgress & {
  instance: string;
  /** The task the update belongs to is running (a copy that is moving now). */
  active: boolean;
  /** A disk the transfer knows about that has not reported yet. */
  waiting?: boolean;
};

export type InstanceProgress = {
  instance: string;
  phase: Phase;
  disks: DiskRow[];
  doneBytes: number;
  totalBytes: number;
  percent: number | null;
  rateBytesPerSec: number;
  etaSeconds: number | null;
};

export type ExecutionProgress = {
  status: string;
  phase: Phase;
  /** The work phases this execution goes through, in order (the stepper). */
  phases: WorkPhase[];
  /** Index of `phase` in `phases`; phases.length once completed, -1 while queued. */
  phaseIndex: number;
  instances: InstanceProgress[];
  disks: DiskRow[];
  hasDiskData: boolean;
  doneBytes: number;
  totalBytes: number;
  /** Bytes copied over bytes to copy, when disks report; else null. */
  bytesPercent: number | null;
  /** Share of the execution's tasks that finished. */
  taskPercent: number;
  /** The figure for the overall bar: bytes while there are disks, tasks otherwise. */
  percent: number;
  /** Sum of the rates of the disks copying now. */
  rateBytesPerSec: number;
  /** Remaining bytes over the combined rate. */
  etaSeconds: number | null;
  activeDisks: number;
  completedDisks: number;
  tasksDone: number;
  tasksTotal: number;
  startedAt: number | null;
  elapsedSeconds: number | null;
};

// ── Parsing ───────────────────────────────────────────────────────────────

const num = (s: string) => parseFloat(s.replace(/,/g, ""));

/** "29.9 GiB" / "512 MiB" -> bytes; "?" -> null. */
export const parseSize = (text: string): number | null => {
  const m = /^\s*([\d.,]+)\s*(B|KiB|MiB|GiB|TiB)\s*$/.exec(text);
  if (!m) {
    return null;
  }
  return num(m[1]) * UNITS[m[2]];
};

/** "1 h 05 min" / "35 min" / "40 s" -> seconds. */
export const parseDuration = (text: string): number | null => {
  const t = text.trim();
  let m = /^(\d+)\s*h\s*(\d+)\s*min$/.exec(t);
  if (m) {
    return Number(m[1]) * 3600 + Number(m[2]) * 60;
  }
  m = /^(\d+)\s*h$/.exec(t);
  if (m) {
    return Number(m[1]) * 3600;
  }
  m = /^(\d+)\s*min$/.exec(t);
  if (m) {
    return Number(m[1]) * 60;
  }
  m = /^(\d+)\s*s$/.exec(t);
  if (m) {
    return Number(m[1]);
  }
  return null;
};

/** "12.4 of 29.9 GiB" / "512 MiB of 900 MiB" / "0 MiB of ?" -> [done, total]. */
const parsePair = (text: string): [number, number | null] | null => {
  const m = /^([\d.,]+)(?:\s*(B|KiB|MiB|GiB|TiB))? of (\?|[\d.,]+\s*(?:B|KiB|MiB|GiB|TiB))$/.exec(
    text.trim(),
  );
  if (!m) {
    return null;
  }
  const total = m[3] === "?" ? null : parseSize(m[3]);
  let unit = m[2];
  if (!unit) {
    const totalUnit = /(B|KiB|MiB|GiB|TiB)$/.exec(m[3]);
    unit = totalUnit ? totalUnit[1] : "GiB";
  }
  return [num(m[1]) * UNITS[unit], total];
};

const RATE_RE = /^([\d.,]+)\s*(KiB|MiB|GiB)\/s$/;
const PREFIX_RE =
  /^([A-Z][a-z]+) (.+?) \((\?|[\d.,]+ (?:B|KiB|MiB|GiB|TiB)) disk, ([^()]*)\): (.+)$/s;
const COMPLETE_RE =
  /^complete, (\S+ (?:B|KiB|MiB|GiB|TiB)) (\w+) in (.+?) \(([\d.,]+) (KiB|MiB|GiB)\/s\)$/;
const SEPARATOR_RE = /\s+[·•|]\s+/;

/** One of the disk messages above -> its figures, or null for any other message. */
export const parseProgressMessage = (
  message: string | null | undefined,
): DiskProgress | null => {
  if (!message) {
    return null;
  }
  const head = PREFIX_RE.exec(message.trim());
  if (!head) {
    return null;
  }
  const [, action, disk, sizeText, modeText, body] = head;
  const commaAt = modeText.indexOf(", ");
  const mode = (commaAt === -1 ? modeText : modeText.slice(0, commaAt)).trim();
  const via = commaAt === -1 ? null : modeText.slice(commaAt + 2).trim() || null;
  const diskSizeBytes = sizeText === "?" ? null : parseSize(sizeText);
  const base = {
    action,
    scanning: action === "Scanning",
    disk,
    diskSizeBytes,
    mode,
    via,
  };

  const done = COMPLETE_RE.exec(body.trim());
  if (done) {
    const copied = parseSize(done[1]) || 0;
    return {
      ...base,
      position: false,
      doneBytes: copied,
      totalBytes: copied,
      percent: 100,
      rateBytesPerSec: num(done[4]) * UNITS[done[5]],
      etaSeconds: 0,
      complete: true,
      durationSeconds: parseDuration(done[3]),
    };
  }

  const parts = body.trim().split(SEPARATOR_RE);
  const what = parts[0];
  let position = false;
  let pairText: string | null = null;
  let percent: number | null = null;
  let m = /^at (.+) of the disk \((\d+(?:\.\d+)?)%\)$/.exec(what);
  if (m) {
    position = true;
    pairText = m[1];
    percent = Number(m[2]);
  } else {
    m = /^(.+) ([a-z]+) \((\d+(?:\.\d+)?)%\)$/.exec(what);
    if (m) {
      pairText = m[1];
      percent = Number(m[3]);
    }
  }
  const pair = pairText ? parsePair(pairText) : null;
  if (!pair || percent == null) {
    return null;
  }
  let rateBytesPerSec: number | null = null;
  let etaSeconds: number | null = null;
  parts.slice(1).forEach(part => {
    const p = part.trim();
    const rate = RATE_RE.exec(p);
    if (rate) {
      rateBytesPerSec = num(rate[1]) * UNITS[rate[2]];
      return;
    }
    const left = /^~?(.+) left$/.exec(p);
    if (left) {
      etaSeconds = parseDuration(left[1]);
    }
  });
  const [doneBytes, totalBytes] = pair;
  return {
    ...base,
    position,
    doneBytes,
    totalBytes,
    percent: Math.max(0, Math.min(100, percent)),
    rateBytesPerSec,
    etaSeconds,
    complete: false,
    durationSeconds: null,
  };
};

/**
 * A progress update -> its disk figures, with the exact byte counts of current_step /
 * total_steps when they agree with the (rounded) message.
 */
export const parseProgressUpdate = (
  update: ProgressUpdate | null | undefined,
): DiskProgress | null => {
  if (!update) {
    return null;
  }
  const parsed = parseProgressMessage(update.message);
  if (!parsed) {
    return null;
  }
  const { current_step: step, total_steps: total } = update;
  if (
    total != null &&
    step != null &&
    total > MIB &&
    step >= 0 &&
    step <= total &&
    parsed.totalBytes != null &&
    Math.abs(total - parsed.totalBytes) <= Math.max(0.06 * GIB, total * 0.01)
  ) {
    if (parsed.complete) {
      return parsed;
    }
    const doneBytes = step;
    const percent = Math.min(100, (step / total) * 100);
    const etaSeconds =
      parsed.etaSeconds == null && parsed.rateBytesPerSec
        ? (total - step) / parsed.rateBytesPerSec
        : parsed.etaSeconds;
    return { ...parsed, doneBytes, totalBytes: total, percent, etaSeconds };
  }
  if (parsed.etaSeconds == null && !parsed.complete && parsed.rateBytesPerSec && parsed.totalBytes) {
    return {
      ...parsed,
      etaSeconds: Math.max(0, parsed.totalBytes - parsed.doneBytes) / parsed.rateBytesPerSec,
    };
  }
  return parsed;
};

// ── Phases ────────────────────────────────────────────────────────────────

/** A Coriolis task type -> the human phase it belongs to. */
export const phaseForTaskType = (taskType: string | null | undefined): WorkPhase => {
  const t = (taskType || "").toUpperCase();
  if (t.startsWith("VALIDATE")) {
    return "Validating";
  }
  if (t.includes("REPLICATE")) {
    return "Syncing disks";
  }
  if (t.includes("OS_MORPHING") || t.includes("OSMORPHING")) {
    return "OS preparation";
  }
  if (t.startsWith("FINALIZE") || t.startsWith("FINALISE")) {
    return "Deploying";
  }
  if (
    t.startsWith("DELETE") ||
    t.startsWith("CLEANUP") ||
    t.startsWith("RELEASE") ||
    t.startsWith("DETACH") ||
    t.startsWith("POWER_OFF") ||
    t.startsWith("RESTORE")
  ) {
    return "Finalising";
  }
  return "Preparing";
};

const RUNNING_TASK = ["RUNNING", "STARTING", "CANCELLING", "CANCELLING_AFTER_COMPLETION"];
const DONE_TASK = ["COMPLETED", "CANCELED_AFTER_COMPLETION"];
/** Tasks that were never going to run (on-error-only steps of a run that did not fail, ...). */
const SKIPPED_TASK = ["UNSCHEDULED", "ON_ERROR_ONLY"];
const FAILED_TASK = ["ERROR", "FAILED_TO_SCHEDULE", "DEADLOCKED", "FAILED_TO_CANCEL"];

export const ACTIVE_EXECUTION_STATUSES = [
  "RUNNING",
  "PENDING",
  "CANCELLING",
  "AWAITING_MINION_ALLOCATIONS",
];
export const isActiveStatus = (status: string | null | undefined) =>
  ACTIVE_EXECUTION_STATUSES.includes(String(status || "").toUpperCase());
export const isFailedStatus = (status: string | null | undefined) =>
  ["ERROR", "DEADLOCKED", "FAILED_TO_SCHEDULE", "ERROR_ALLOCATING_MINIONS"].includes(
    String(status || "").toUpperCase(),
  );
export const isCancelledStatus = (status: string | null | undefined) =>
  ["CANCELED", "FORCE_CANCELED", "CANCELED_FOR_DEBUGGING"].includes(
    String(status || "").toUpperCase(),
  );

const phaseOrder = (p: WorkPhase) => PHASES.indexOf(p);

/** The phase a set of tasks is in: the least advanced running one, else the next to run. */
const currentPhase = (tasks: Task[], status?: string): Phase => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETED") {
    return "Completed";
  }
  if (isFailedStatus(s)) {
    return "Failed";
  }
  if (isCancelledStatus(s)) {
    return "Cancelled";
  }
  const scheduled = tasks.filter(t => !SKIPPED_TASK.includes(t.status));
  const running = scheduled.filter(t => RUNNING_TASK.includes(t.status));
  const lowest = (list: Task[]) =>
    list
      .map(t => phaseForTaskType(t.task_type))
      .sort((a, b) => phaseOrder(a) - phaseOrder(b))[0];
  if (running.length) {
    return lowest(running);
  }
  const done = scheduled.filter(t => DONE_TASK.includes(t.status));
  const pending = scheduled.filter(
    t => !DONE_TASK.includes(t.status) && !FAILED_TASK.includes(t.status),
  );
  if (!done.length && (s === "PENDING" || s === "AWAITING_MINION_ALLOCATIONS" || !tasks.length)) {
    return "Queued";
  }
  if (pending.length) {
    return lowest(pending);
  }
  if (scheduled.some(t => FAILED_TASK.includes(t.status))) {
    return "Failed";
  }
  return done.length ? phaseForTaskType(done[done.length - 1].task_type) : "Queued";
};

// ── Aggregation ───────────────────────────────────────────────────────────

export type KnownDisk = { name?: string | null; id?: string | null; sizeBytes: number | null };

/** Latest figures per disk of a set of tasks (a "Transferring" line wins over its "Scanning" pass). */
const diskRows = (tasks: Task[]): DiskRow[] => {
  const byKey = new Map<string, { row: DiskRow; index: number }>();
  tasks.forEach(task => {
    (task.progress_updates || []).forEach((update, n) => {
      const parsed = parseProgressUpdate(update);
      if (!parsed) {
        return;
      }
      const index = update.index != null ? update.index : n;
      const key = `${task.instance}\u0000${parsed.disk}`;
      const row: DiskRow = {
        ...parsed,
        instance: task.instance,
        active: RUNNING_TASK.includes(task.status) && !parsed.complete,
      };
      const seen = byKey.get(key);
      if (seen) {
        // A scan line never replaces the copy line of the same disk; otherwise the latest wins.
        if (!seen.row.scanning && row.scanning) {
          return;
        }
        if (seen.row.scanning === row.scanning && index < seen.index) {
          return;
        }
      }
      byKey.set(key, { row, index });
    });
  });
  return Array.from(byKey.values()).map(v => v.row);
};

const sum = (list: number[]) => list.reduce((a, b) => a + b, 0);

const rowTotal = (d: DiskRow) =>
  d.complete ? d.doneBytes : d.totalBytes != null ? d.totalBytes : d.diskSizeBytes || 0;

/** "/dev/sda" and "sda (live pass)" are the same disk. */
const diskKey = (s: string) =>
  s
    .trim()
    .replace(/^\/dev\//, "")
    .replace(/\s*\([^()]*\)$/, "")
    .toLowerCase();

const matchesKnown = (row: DiskRow, known: KnownDisk) => {
  const names = [known.name, known.id].filter(Boolean).map(n => String(n));
  const rowKey = diskKey(row.disk);
  return names.some(
    n => row.disk === n || row.disk.includes(n) || n.includes(row.disk) || (rowKey && diskKey(n) === rowKey),
  );
};

const summariseInstance = (
  instance: string,
  tasks: Task[],
  rows: DiskRow[],
  status: string,
  known?: KnownDisk[],
): InstanceProgress => {
  const phase = currentPhase(tasks, isActiveStatus(status) ? "RUNNING" : status);
  const disks = rows.slice();
  const syncs = tasks.some(t => phaseForTaskType(t.task_type) === "Syncing disks");
  const beforeSyncEnds =
    isActiveStatus(status) &&
    ["Queued", "Validating", "Preparing", "Syncing disks"].includes(phase);
  if (known && known.length && syncs && beforeSyncEnds) {
    const copyRows = disks.filter(d => !d.scanning);
    const unmatched = known.filter(k => !copyRows.some(r => matchesKnown(r, k)));
    const missing = Math.max(0, known.length - disks.length);
    unmatched.slice(0, missing).forEach(k => {
      disks.push({
        action: "Transferring",
        scanning: false,
        disk: String(k.name || k.id || "disk"),
        diskSizeBytes: k.sizeBytes,
        mode: "",
        via: null,
        position: false,
        doneBytes: 0,
        totalBytes: k.sizeBytes,
        percent: 0,
        rateBytesPerSec: null,
        etaSeconds: null,
        complete: false,
        durationSeconds: null,
        instance,
        active: false,
        waiting: true,
      });
    });
  }
  const counted = disks.filter(d => !d.scanning);
  const doneBytes = sum(counted.map(d => Math.min(d.doneBytes, rowTotal(d) || d.doneBytes)));
  const totalBytes = sum(counted.map(rowTotal));
  const rateBytesPerSec = sum(counted.filter(d => d.active).map(d => d.rateBytesPerSec || 0));
  const remaining = Math.max(0, totalBytes - doneBytes);
  return {
    instance,
    phase,
    disks,
    doneBytes,
    totalBytes,
    percent: totalBytes > 0 ? Math.min(100, (doneBytes / totalBytes) * 100) : null,
    rateBytesPerSec,
    etaSeconds: rateBytesPerSec > 0 ? remaining / rateBytesPerSec : remaining === 0 ? 0 : null,
  };
};

/** "2026-09-30T08:56:47.000000" (UTC, no zone) -> epoch ms. */
export const parseApiDate = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const s = String(value).trim();
  const zoned = /([zZ]|[+-]\d\d:?\d\d)$/.test(s) ? s : `${s.replace(" ", "T")}Z`;
  const t = Date.parse(zoned);
  return Number.isNaN(t) ? null : t;
};

export type ExecutionLike = {
  status: string;
  tasks?: Task[] | null;
  created_at?: string | null;
  updated_at?: string | null;
  type?: string;
};

/**
 * An execution (or a deployment with its tasks) -> where it is: phase, bytes copied of bytes to
 * copy, combined throughput of the disks copying now, time left, and each disk.
 * `knownDisks` (instance -> the transfer's disks) adds rows for disks that have not reported yet.
 */
export const summariseExecution = (
  execution: ExecutionLike,
  options?: { now?: number; knownDisks?: { [instance: string]: KnownDisk[] } },
): ExecutionProgress => {
  const now = options?.now ?? Date.now();
  const tasks = execution.tasks || [];
  const status = String(execution.status || "").toUpperCase();
  const rows = diskRows(tasks);

  const instanceNames: string[] = [];
  tasks.forEach(t => {
    if (!instanceNames.includes(t.instance)) {
      instanceNames.push(t.instance);
    }
  });
  const instances = instanceNames.map(name =>
    summariseInstance(
      name,
      tasks.filter(t => t.instance === name),
      rows.filter(r => r.instance === name),
      status,
      options?.knownDisks?.[name],
    ),
  );
  const disks = instances.flatMap(i => i.disks);
  const counted = disks.filter(d => !d.scanning);

  const phase = currentPhase(tasks, status);
  const presentPhases = new Set(
    tasks.filter(t => !SKIPPED_TASK.includes(t.status)).map(t => phaseForTaskType(t.task_type)),
  );
  let phases = PHASES.filter(p => presentPhases.has(p)) as WorkPhase[];
  if (!phases.length) {
    phases =
      execution.type === "transfer_deploy" || execution.type === "deployment"
        ? ["Validating", "Preparing", "OS preparation", "Deploying", "Finalising"]
        : ["Validating", "Preparing", "Syncing disks", "Finalising"];
  }
  let phaseIndex: number;
  if (phase === "Completed") {
    phaseIndex = phases.length;
  } else if (phase === "Queued") {
    phaseIndex = -1;
  } else if (phase === "Failed" || phase === "Cancelled") {
    // Where it stopped: the phase of the task that failed, else the last one that ran.
    const stopped =
      tasks.find(t => FAILED_TASK.includes(t.status)) ||
      tasks.filter(t => DONE_TASK.includes(t.status) || RUNNING_TASK.includes(t.status)).pop();
    phaseIndex = stopped ? phases.indexOf(phaseForTaskType(stopped.task_type)) : 0;
  } else {
    phaseIndex = phases.indexOf(phase as WorkPhase);
  }

  const doneBytes = sum(instances.map(i => i.doneBytes));
  const totalBytes = sum(instances.map(i => i.totalBytes));
  const rateBytesPerSec = sum(instances.map(i => i.rateBytesPerSec));
  const remaining = Math.max(0, totalBytes - doneBytes);
  const hasDiskData = counted.some(d => !d.waiting);

  const scheduled = tasks.filter(t => !SKIPPED_TASK.includes(t.status));
  const tasksDone = scheduled.filter(t => DONE_TASK.includes(t.status)).length;
  const tasksTotal = scheduled.length;
  const taskPercent =
    status === "COMPLETED" ? 100 : tasksTotal ? (tasksDone / tasksTotal) * 100 : 0;
  const bytesPercent = hasDiskData && totalBytes > 0 ? Math.min(100, (doneBytes / totalBytes) * 100) : null;
  const percent =
    status === "COMPLETED" ? 100 : bytesPercent != null ? bytesPercent : taskPercent;

  let etaSeconds: number | null = null;
  if (isActiveStatus(status) && hasDiskData) {
    if (rateBytesPerSec > 0) {
      etaSeconds = remaining / rateBytesPerSec;
    } else if (remaining === 0) {
      etaSeconds = 0;
    }
  }

  const startedAt = parseApiDate(execution.created_at);
  const endedAt = isActiveStatus(status) ? now : parseApiDate(execution.updated_at) || now;
  return {
    status,
    phase,
    phases,
    phaseIndex,
    instances,
    disks,
    hasDiskData,
    doneBytes,
    totalBytes,
    bytesPercent,
    taskPercent,
    percent,
    rateBytesPerSec,
    etaSeconds,
    activeDisks: counted.filter(d => d.active).length,
    completedDisks: counted.filter(d => d.complete).length,
    tasksDone,
    tasksTotal,
    startedAt,
    elapsedSeconds: startedAt != null ? Math.max(0, (endedAt - startedAt) / 1000) : null,
  };
};

/** Bytes a finished execution copied (its complete disk lines), for "data moved" figures. */
export const copiedBytes = (tasks: Task[] | null | undefined): number =>
  sum(
    diskRows(tasks || [])
      .filter(d => !d.scanning)
      .map(d => d.doneBytes),
  );

// ── Formatting ────────────────────────────────────────────────────────────

/** 29.9 GiB, 1.2 TiB, 512 MiB. */
export const formatBytes = (bytes: number | null | undefined, digits = 1): string => {
  if (bytes == null || Number.isNaN(bytes)) {
    return "—";
  }
  if (bytes >= TIB) {
    return `${(bytes / TIB).toFixed(digits)} TiB`;
  }
  if (bytes >= GIB / 10) {
    return `${(bytes / GIB).toFixed(digits)} GiB`;
  }
  if (bytes >= MIB) {
    return `${Math.round(bytes / MIB)} MiB`;
  }
  if (bytes > 0) {
    return `${Math.max(1, Math.round(bytes / KIB))} KiB`;
  }
  return "0 GiB";
};

/** 412 MiB/s, 1.2 GiB/s, 8.6 MiB/s. */
export const formatRate = (bytesPerSec: number | null | undefined): string => {
  if (bytesPerSec == null || Number.isNaN(bytesPerSec)) {
    return "—";
  }
  if (bytesPerSec >= GIB) {
    return `${(bytesPerSec / GIB).toFixed(2)} GiB/s`;
  }
  const mib = bytesPerSec / MIB;
  if (mib >= 100) {
    return `${Math.round(mib)} MiB/s`;
  }
  if (mib >= 0.05) {
    return `${mib.toFixed(1)} MiB/s`;
  }
  return bytesPerSec > 0 ? `${Math.max(1, Math.round(bytesPerSec / KIB))} KiB/s` : "0 MiB/s";
};

/** 40 s, 35 min, 1 h 05 min, 2 d 3 h. */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(seconds) || !Number.isFinite(seconds)) {
    return "—";
  }
  const s = Math.max(0, Math.round(seconds));
  if (s >= 86400) {
    return `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} h`;
  }
  if (s >= 3600) {
    return `${Math.floor(s / 3600)} h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")} min`;
  }
  if (s >= 60) {
    return `${Math.floor(s / 60)} min`;
  }
  return `${s} s`;
};
